import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";
import {apiGenericCreate, apiGenericUpdate, genericGetOne} from "../generic/crud";
import {
    ENTITY_ID_JOB_CARD,
    ENTITY_ID_BRANCH,
    ENTITY_ID_VEHICLE_MODEL,
    ENTITY_ID_ADVISORS,
    ENTITY_ID_TREATMENT,
    ENTITY_ID_DEALER
} from "../generic/Constants";
import {defaultJobCard} from "../generic/InitialStates";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {AGCText} from "../../generic-components/AGCText";
import {AGCSelect} from "../../generic-components/AGCSelect";
import {AGCStaticSelect} from "../../generic-components/AGCStaticSelect";
import Select from 'react-select';
import {genericSelectGetAll} from "../generic/crud";
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

const JOB_TYPES = [
    {id: 'FREE_SERVICE', name: 'Free Service'},
    {id: 'PAID_SERVICE', name: 'Paid Service'},
    {id: 'RUNNING_REPAIR', name: 'Running Repair'},
    {id: 'ACCIDENTAL_REPAIR', name: 'Accidental Repair'}
];

const STATUS_TYPES = [
    {id: 'OPEN', name: 'Open'},
    {id: 'IN_PROGRESS', name: 'In Progress'},
    {id: 'CLOSED', name: 'Closed'}
];

export function JobCardAddEditDialog(props) {
    const {show, handleClose, id, onSave, preSelectedBranch} = props;
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    const [forUpdate, setForUpdate] = useState(false);
    const [objectForSubmit, setObjectForSubmit] = useState(defaultJobCard);
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState("New Job Card");
    const [dealerId, setDealerId] = useState(null);
    const [dealerBrand, setDealerBrand] = useState(null);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [branchAdvisorIds, setBranchAdvisorIds] = useState([]);
    const [allAdvisors, setAllAdvisors] = useState([]);
    const [filteredAdvisors, setFilteredAdvisors] = useState([]);
    const [existingTreatments, setExistingTreatments] = useState([]);
    const [newTreatments, setNewTreatments] = useState([]);
    const entityName = "Job Card";

    const formik = useFormik({
        initialValues: objectForSubmit,
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            branchId: Yup.mixed().required("Branch is required"),
            vehicleNo: Yup.string().required("Vehicle Number is required"),
            vehicleModelId: Yup.mixed().required("Vehicle Model is required"),
            roNumber: Yup.string().required("RO Number is required"),
            jobType: Yup.mixed().required("Job Type is required"),
            advisorId: Yup.mixed().required("Service Advisor is required"),
            committedDate: Yup.date().required("Committed Date is required"),
            selectedTreatments: Yup.array().test(
                'has-treatments',
                'At least 1 treatment is required',
                function(value) {
                    // For create mode, require at least 1
                    // For edit mode, allow empty if existingTreatments exist
                    return (value && value.length > 0) || existingTreatments.length > 0;
                }
            ),
        }),
        onSubmit(values, {resetForm}) {
            setIsLoading(true);
            // Combine existing treatments with new additions
            const allTreatments = [
                ...existingTreatments,
                ...(values.selectedTreatments || [])
            ];

            const submissionData = {
                ...values,
                branchId: values.branchId?.id || values.branchId,
                vehicleModelId: values.vehicleModelId?.id || values.vehicleModelId,
                advisorId: values.advisorId?.id || values.advisorId,
                jobType: values.jobType?.id || values.jobType,
                selectedTreatments: allTreatments.map(t => ({...t, id: t.id || t}))
            };
            
             const config = { suppressToast: true };

            if (!id) {
                apiGenericCreate(ENTITY_ID_JOB_CARD, submissionData, config).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Created!", TOAST_SUCCESS);
                    resetForm();
                    if (onSave) onSave();
                }).catch((error) => {
                    setIsLoading(false);
                     showToast(error?.response?.data?.message || "Error creating Job Card", TOAST_FAILURE);
                });
            } else {
                apiGenericUpdate(ENTITY_ID_JOB_CARD, submissionData, id, config).then(() => {
                    setIsLoading(false);
                    handleClose();
                    showToast(entityName + " is Updated!", TOAST_SUCCESS);
                    resetForm();
                    if (onSave) onSave();
                }).catch((error) => {
                    setIsLoading(false);
                     showToast(error?.response?.data?.message || "Error updating Job Card", TOAST_FAILURE);
                });
            }
        }
    });

    useEffect(() => {
        if (id != null) {
            setForUpdate(true);
            setTitle("Edit Job Card");
            setIsLoading(true);

            genericGetOne(ENTITY_ID_JOB_CARD, id).then(
                async ({data}) => {
                    // Fetch related entities to populate select fields
                    const enrichedData = {...data};

                    // Fetch branch details
                    if (data.branchId) {
                        try {
                            const {data: branchData} = await genericGetOne(ENTITY_ID_BRANCH, data.branchId);
                            enrichedData.branchId = {id: branchData.id, name: branchData.name};
                        } catch (e) {
                            enrichedData.branchId = data.branchId;
                        }
                    }

                    // Fetch advisor details
                    if (data.advisorId) {
                        try {
                            const {data: advisorData} = await genericGetOne(ENTITY_ID_ADVISORS, data.advisorId);
                            enrichedData.advisorId = {id: advisorData.id, name: advisorData.name};
                        } catch (e) {
                            enrichedData.advisorId = data.advisorId;
                        }
                    }

                    // Fetch vehicle model details
                    if (data.vehicleModelId) {
                        try {
                            const {data: modelData} = await genericGetOne(ENTITY_ID_VEHICLE_MODEL, data.vehicleModelId);
                            enrichedData.vehicleModelId = {id: modelData.id, name: modelData.oem + " - " + modelData.model};
                        } catch (e) {
                            enrichedData.vehicleModelId = data.vehicleModelId;
                        }
                    }

                    // Store existing treatments separately for read-only display
                    setExistingTreatments(data.selectedTreatments || []);

                    // Convert jobType string to object for select component
                    if (data.jobType && typeof data.jobType === 'string') {
                        const jobTypeObj = JOB_TYPES.find(jt => jt.id === data.jobType);
                        enrichedData.jobType = jobTypeObj || data.jobType;
                    }

                    setObjectForSubmit({
                         ...enrichedData,
                         selectedTreatments: [], // Clear for new additions only
                         committedDate: data.committedDate ? new Date(data.committedDate) : new Date(),
                         startDate: data.startDate ? new Date(data.startDate) : new Date(),
                         endDate: data.endDate ? new Date(data.endDate) : null,
                    });
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        } else {
            setForUpdate(false);
            setExistingTreatments([]); // Clear existing treatments for create mode

            const eodToday = new Date();
            eodToday.setHours(18, 0, 0, 0);

            const initialData = {
                ...defaultJobCard,
                jobType: JOB_TYPES.find(jt => jt.id === 'PAID_SERVICE'),
                status: 'OPEN',
                committedDate: eodToday,
                startDate: new Date()
            };

            if (preSelectedBranch) {
                // Extract branch ID - handle both object and string formats
                let branchId = null;
                if (typeof preSelectedBranch === 'object' && preSelectedBranch.id) {
                    branchId = preSelectedBranch.id;
                } else if (typeof preSelectedBranch === 'string') {
                    branchId = preSelectedBranch;
                }

                if (branchId && typeof branchId === 'string') {
                    // Fetch the full branch object to populate the select properly
                    genericGetOne(ENTITY_ID_BRANCH, branchId).then(({data}) => {
                        initialData.branchId = {id: data.id, name: data.name};
                        setObjectForSubmit(initialData);
                    }).catch(() => {
                        // If fetch fails, just use the ID
                        initialData.branchId = preSelectedBranch;
                        setObjectForSubmit(initialData);
                    });
                } else {
                    setObjectForSubmit(initialData);
                }
            } else {
                setObjectForSubmit(initialData);
            }

            setTitle("New Job Card");
        }
    }, [show, id, preSelectedBranch]);
    
    // Format date for datetime-local input
    const formatDateForInput = (date) => {
        if (!date) return '';
        try {
             // adjust to local time for input
            const d = new Date(date);
            d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
            return d.toISOString().slice(0, 16);
        } catch(e) {
            return '';
        }
    };
    
    const handleDateChange = (e, field) => {
        formik.setFieldValue(field, new Date(e.target.value));
    };

    // Custom styles for dark theme
    const customSelectStyles = isDarkMode ? {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: '#1e1e2d',
            borderColor: state.isFocused ? '#009ef7' : '#2b2b40',
            color: '#a1a5b7',
            '&:hover': {
                borderColor: '#009ef7'
            }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#1e1e2d',
            border: '1px solid #2b2b40'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#2b2b40' : '#1e1e2d',
            color: '#a1a5b7',
            '&:hover': {
                backgroundColor: '#2b2b40'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#a1a5b7'
        }),
        input: (provided) => ({
            ...provided,
            color: '#a1a5b7'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c7293'
        })
    } : {};

    // Fetch all advisors on component mount
    useEffect(() => {
        if (show) {
            genericSelectGetAll(ENTITY_ID_ADVISORS).then(({data}) => {
                const safeData = Array.isArray(data) ? data : [];
                setAllAdvisors(safeData);
            }).catch(() => {
                setAllAdvisors([]);
            });
        }
    }, [show]);

    // Filter advisors based on branch's advisorIds
    useEffect(() => {
        if (branchAdvisorIds.length > 0 && allAdvisors.length > 0) {
            const filtered = allAdvisors.filter(advisor =>
                branchAdvisorIds.includes(advisor.id)
            );
            setFilteredAdvisors(filtered);
        } else {
            setFilteredAdvisors([]);
        }
    }, [branchAdvisorIds, allAdvisors]);

    // Fetch dealerId and dealerBrand when branch changes
    useEffect(() => {
        // Extract branch ID - handle both object and string formats
        const extractId = (value) => {
            if (!value) return null;
            if (typeof value === 'string') return value;
            if (typeof value === 'object') {
                // Handle nested objects recursively
                if (value.id) return extractId(value.id);
            }
            return null;
        };

        const branchId = extractId(formik.values.branchId);

        if (branchId && typeof branchId === 'string' && branchId !== '[object Object]') {
            setSelectedBranchId(branchId);
            genericGetOne(ENTITY_ID_BRANCH, branchId).then(({data: branchData}) => {
                if (branchData) {
                    // Store advisor IDs from the branch
                    if (branchData.advisorIds && Array.isArray(branchData.advisorIds)) {
                        setBranchAdvisorIds(branchData.advisorIds);
                    } else {
                        setBranchAdvisorIds([]);
                    }

                    if (branchData.dealerId) {
                        setDealerId(branchData.dealerId);

                        // Fetch dealer to get brand
                        genericGetOne(ENTITY_ID_DEALER, branchData.dealerId).then(({data: dealerData}) => {
                            if (dealerData && dealerData.brand) {
                                setDealerBrand(dealerData.brand);
                            }
                        }).catch(() => {
                            setDealerBrand(null);
                        });
                    }
                }
            }).catch(() => {
                setDealerId(null);
                setDealerBrand(null);
                setBranchAdvisorIds([]);
            });
        } else {
            setSelectedBranchId(null);
            setDealerId(null);
            setDealerBrand(null);
            setBranchAdvisorIds([]);
        }
    }, [formik.values.branchId]);

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-lg"
            fullscreen="sm-down"
        >
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading && (
                    <Form className="form" onSubmit={(e) => e.preventDefault()}>
                        <div className="py-2">
                            
                            {/* Section 1: Location & Personnel */}
                            <div className="card shadow-sm border border-gray-300 mb-5 rounded-3">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="symbol symbol-30px me-3">
                                            <span className="symbol-label bg-light-primary text-primary">
                                                <i className="bi bi-geo-alt-fill fs-6"></i>
                                            </span>
                                        </div>
                                        <h5 className="fw-bold text-gray-800 mb-0">Location & Advisor</h5>
                                    </div>
                                    
                                    <div className="row g-4">
                                        <Form.Group className="col-12 col-md-6">
                                            <AGCSelect
                                                formik={formik}
                                                name="Branch"
                                                id="branchId"
                                                entityId={ENTITY_ID_BRANCH}
                                                value={formik.values.branchId}
                                                toolTip="Select Branch"
                                                forUpdate={!!preSelectedBranch}
                                            />
                                        </Form.Group>
                                        <Form.Group className="col-12 col-md-6">
                                            {!selectedBranchId && (
                                                <div className="alert alert-warning d-flex align-items-center mb-2 py-2">
                                                    <i className="bi bi-exclamation-triangle-fill me-2 fs-7"></i>
                                                    <span className="fs-8">Please select a branch first</span>
                                                </div>
                                            )}
                                            <Form.Label className="required">Service Advisor</Form.Label>
                                            <Select
                                                options={filteredAdvisors}
                                                value={formik.values.advisorId}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                onChange={option => formik.setFieldValue('advisorId', option)}
                                                styles={customSelectStyles}
                                                placeholder="Select Advisor"
                                                isClearable
                                                isDisabled={!selectedBranchId || filteredAdvisors.length === 0}
                                            />
                                            <Form.Text className="text-muted">
                                                {selectedBranchId && filteredAdvisors.length === 0 && "No advisors assigned to this branch"}
                                                {!selectedBranchId && "Select a branch to see available advisors"}
                                            </Form.Text>
                                            {formik.touched.advisorId && formik.errors.advisorId && (
                                                <div className="text-danger fs-8 mt-1">{formik.errors.advisorId}</div>
                                            )}
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Vehicle Details */}
                            <div className="card shadow-sm border border-gray-300 mb-5 rounded-3">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="symbol symbol-30px me-3">
                                            <span className="symbol-label bg-light-warning text-warning">
                                                <i className="bi bi-truck fs-6"></i>
                                            </span>
                                        </div>
                                        <h5 className="fw-bold text-gray-800 mb-0">Vehicle Information</h5>
                                    </div>

                                    <div className="row g-4">
                                        <Form.Group className="col-12 col-md-6">
                                            <AGCText
                                                formik={formik}
                                                name="Vehicle Number"
                                                id="vehicleNo"
                                                placeHolder="e.g. KA01AB1234"
                                                value={formik.values.vehicleNo}
                                            />
                                        </Form.Group>
                                        <Form.Group className="col-12 col-md-6">
                                            {!dealerBrand && (
                                                <div className="alert alert-warning d-flex align-items-center mb-2 py-2">
                                                    <i className="bi bi-exclamation-triangle-fill me-2 fs-7"></i>
                                                    <span className="fs-8">Please select a branch first</span>
                                                </div>
                                            )}
                                            <AGCSelect
                                                formik={formik}
                                                name="Vehicle Model"
                                                id="vehicleModelId"
                                                entityId={ENTITY_ID_VEHICLE_MODEL}
                                                value={formik.values.vehicleModelId}
                                                toolTip="Select Vehicle Model"
                                                filterField={dealerBrand ? "oem" : null}
                                                filterValue={dealerBrand}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Job Details */}
                            <div className="card shadow-sm border border-gray-300 mb-5 rounded-3">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="symbol symbol-30px me-3">
                                            <span className="symbol-label bg-light-info text-info">
                                                <i className="bi bi-file-earmark-text-fill fs-6"></i>
                                            </span>
                                        </div>
                                        <h5 className="fw-bold text-gray-800 mb-0">Job Details</h5>
                                    </div>

                                    <div className="row g-4">
                                        <Form.Group className="col-12 col-md-6">
                                            <AGCText
                                                formik={formik}
                                                name="RO Number"
                                                id="roNumber"
                                                placeHolder="Enter RO Number"
                                                value={formik.values.roNumber}
                                            />
                                        </Form.Group>
                                        <Form.Group className="col-12 col-md-6">
                                            <AGCStaticSelect
                                                data={JOB_TYPES}
                                                formik={formik}
                                                name="Job Type"
                                                id="jobType"
                                                value={formik.values.jobType}
                                                toolTip="Select Job Type"
                                                includeAllOption={false}
                                            />
                                        </Form.Group>
                                        
                                        <Form.Group className="col-12 col-md-6">
                                            <Form.Label className="required fw-bold fs-6 mb-2">Job Status</Form.Label>
                                            <Form.Select
                                                className="form-select"
                                                name="status"
                                                value={formik.values.status}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            >
                                                {STATUS_TYPES.map(status => (
                                                    <option key={status.id} value={status.id}>{status.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group className="col-12 col-md-6">
                                             <Form.Label className="required fw-bold fs-6 mb-2">Committed Date</Form.Label>
                                             <Form.Control
                                                type="datetime-local"
                                                className="form-control"
                                                name="committedDate"
                                                value={formatDateForInput(formik.values.committedDate)}
                                                onChange={(e) => handleDateChange(e, 'committedDate')}
                                             />
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Section 4: Treatments */}
                            <div className="card shadow-sm border border-gray-300 mb-5 rounded-3">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="symbol symbol-30px me-3">
                                            <span className="symbol-label bg-light-success text-success">
                                                <i className="bi bi-tools fs-6"></i>
                                            </span>
                                        </div>
                                        <h5 className="fw-bold text-gray-800 mb-0">Treatments</h5>
                                    </div>

                                    <div className="row">
                                        <Form.Group className="col-12">
                                            {/* Show existing treatments for edit mode */}
                                            {forUpdate && existingTreatments.length > 0 && (
                                                <div className="mb-3">
                                                    <Form.Label className="fw-bold fs-6 mb-2">Existing Treatments</Form.Label>
                                                    <div className="d-flex flex-wrap gap-2 p-3 bg-light rounded">
                                                        {existingTreatments.map((treatment, index) => (
                                                            <span key={index} className="badge badge-light-primary fs-7 px-3 py-2">
                                                                <i className="bi bi-check-circle me-1"></i>
                                                                {treatment.name || treatment.id}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {!dealerId && (
                                                <div className="alert alert-warning d-flex align-items-center mb-3">
                                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                    <span>Please select a branch first to see available treatments</span>
                                                </div>
                                            )}
                                            <AGCSelect
                                                formik={formik}
                                                isMulti={true}
                                                name={forUpdate ? "Add New Treatments" : "Treatments"}
                                                id="selectedTreatments"
                                                entityId={ENTITY_ID_TREATMENT}
                                                value={formik.values.selectedTreatments}
                                                toolTip={forUpdate ? "Select additional treatments to add" : "Select one or more treatments (at least 1 required)"}
                                                filterField={dealerId ? "dealerId" : null}
                                                filterValue={dealerId}
                                                required={!forUpdate}
                                            />
                                            {formik.touched.selectedTreatments && formik.errors.selectedTreatments && (
                                                <div className="text-danger fs-8 mt-1">{formik.errors.selectedTreatments}</div>
                                            )}
                                        </Form.Group>
                                     </div>
                                </div>
                            </div>

                        </div>

                        <Modal.Footer>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                     formik.validateForm().then(errors => {
                                         if(Object.keys(errors).length > 0) {
                                             showToast("Please fill all mandatory fields", TOAST_FAILURE);
                                             formik.setTouched({
                                                 branchId: true,
                                                 vehicleNo: true,
                                                 vehicleModelId: true,
                                                 roNumber: true,
                                                 jobType: true,
                                                 advisorId: true,
                                                 committedDate: true,
                                                 selectedTreatments: true
                                             });
                                         } else {
                                             formik.handleSubmit();
                                         }
                                     })
                                }}
                                className="btn btn-primary">
                                Save Job Card
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
                {isLoading && (
                    <div className="d-flex justify-content-center align-items-center p-10 min-h-200px">
                         <div className="d-flex flex-column align-items-center">
                             <span className="spinner-border text-primary mb-3"></span>
                             <span className="text-gray-600">Processing...</span>
                         </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
